import { FC, useEffect, useState } from "react";
import styles from "./DraftsTable.module.scss";
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { EditIcon, SideBarThirdIcon } from "customIcons";
import { GlobeSearchIcon, TrashIcon } from "@features/common/SvgIcons";
import RemoveModal from "../removeModal/RemoveModal";
import StyledTooltip from "../toolTip/StyledTooltip";
import { DraftData } from "interfaces/common.interface";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import {
  notifyUser,
  selectUploadListSnapshot,
setUploadListSnapshot,
} from "@redux/slices/CommonSlice";
import {
  useRemoveDraftMutation,
  useSendDraftMutation,
  useSummaryDraftMutation,
} from "../draftsAPI";
import { useNavigate } from "react-router-dom";
import { PAGE_URLS } from "@constants/common";
import ConfirmModal from "../confirmModal/ConfirmModal";
import SubmitStyledTooltip from "../toolTip/SubmitStyledTooltip";
import { formatDate, formatTime } from "@utils/common";
import EmptyTable from "@features/common/emptyTable/EmptyTable";

interface DraftsTableProps {
  tableData: DraftData[];
  darfsTableLoading: boolean;
}

const DraftsTable: FC<DraftsTableProps> = ({
  tableData,
  darfsTableLoading,
}) => {
  /* --------------------------------------------------------------------------
   * State & Hooks
   * -------------------------------------------------------------------------- */
  const [isLoading, setIsLoading] = useState(darfsTableLoading);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedDraftId, setSelectedDraftId] = useState<string | null>(null);

  const urlLists = useAppSelector(selectUploadListSnapshot);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  /* --------------------------------------------------------------------------
   * Derived API Endpoints & Flags
   * -------------------------------------------------------------------------- */
  const cancelUr = urlLists?.cancel;
  const submitUrl = urlLists?.submit;
  const hasData = tableData && tableData.length > 0;

  const [removeDrafts, { isLoading: isRemoving }] = useRemoveDraftMutation();
  const [sendDrafts, { isLoading: isSending }] = useSendDraftMutation();
  const [summaryDrafts, { data: summaryData, isLoading: isSummaryLoading }] =
    useSummaryDraftMutation();

  /* --------------------------------------------------------------------------
   * Side Effects
   * -------------------------------------------------------------------------- */
  // Sync internal loading state with prop updates
  useEffect(() => {
    setIsLoading(darfsTableLoading);
  }, [darfsTableLoading]);

  /* --------------------------------------------------------------------------
   * Event Handlers
   * -------------------------------------------------------------------------- */

  /**
   * Prepares the deletion process by saving action URLs to Redux
   * and opening the confirmation modal.
   */
  const handleRemoveDrafts = (draftId: number) => {
    const selectedDraft = findDraftData(draftId);
    const result = selectedDraft?.actions;

    const actions = {
      cancel: result?.cancel,
      list: result?.list,
      save: result?.save,
      submit: result?.submit,
      summary: result?.summary,
      clear_all: result?.clear_all,
    };

    dispatch(setUploadListSnapshot(actions));
    setIsRemoveModalOpen(true);
    setSelectedDraftId(draftId?.toString() || null);
  };

  /**
   * Resets modal state and clears the selected ID
   */
  const handleCloseRemoveModal = () => {
    setIsRemoveModalOpen(false);
    setSelectedDraftId(null);
  };

  /**
   * Resets confirm modal state and clears the selected data
   */
  const handleCloseConfirmModal = () => {
    setIsConfirmModalOpen(false);
    setSelectedDraftId(null);
  };

  /**
   * Remove the selected draft after the confirmation modal is closed
   * Executes the actual deletion API call using the URL stored in Redux
   */
  const handleConfirmRemove = async () => {
    if (!cancelUr || isRemoving || !selectedDraftId) return;
    try {
      await removeDrafts(cancelUr).unwrap();
      dispatch(setUploadListSnapshot({}));
      handleCloseRemoveModal();
      showSuccessNotification({
        title: "Removed Successfully",
        message: "Your data has been deleted successfully",
      });
    } catch (error) {
      console.error("Failed to remove draft:", error);
    }
  };

  /**
   * Handle confirm action from the confirm modal
   * Executes the actual API call after user confirms
   */
  const handleConfirmSubmit = async () => {
    if (!submitUrl || isSending || !selectedDraftId) return;

    try {
      await sendDrafts({ url: submitUrl, method: "POST" }).unwrap();
      dispatch(setUploadListSnapshot({}));
      handleCloseConfirmModal();
      showSuccessNotification({
        title: "Submit Successfully",
        message: "Your data has been submitted successfully",
      });
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Opens the confirm modal before sending the draft
   * The actual API call happens after user confirms in the modal
   */
  const handleSendDrafts = async (draftId: number) => {
    setSelectedDraftId(draftId?.toString() || null);
    const selectedDraft = findDraftData(draftId);
    const result = selectedDraft?.actions;

    const actions = {
      cancel: result?.cancel,
      list: result?.list,
      save: result?.save,
      submit: result?.submit,
      summary: result?.summary,
      clear_all: result?.clear_all,
    };

    if (!actions?.summary) return;

    try {
      await summaryDrafts(actions?.summary).unwrap();
    } catch (error) {}

    dispatch(setUploadListSnapshot(actions));
    setIsConfirmModalOpen(true);
  };

  /**
   * Stores current draft action URLs in Redux and redirects
   * the user to the credits listing page for editing.
   */
  const handleEditDrafts = (draftId: number) => {
    const selectedDraft = findDraftData(draftId);
    const result = selectedDraft?.actions;

    const action = {
      cancel: result?.cancel,
      list: result?.list,
      save: result?.save,
      submit: result?.submit,
      summary: result?.summary,
      clear_all: result?.clear_all,
    };

    dispatch(setUploadListSnapshot(action));
    navigate(PAGE_URLS.CREDITS_LISTING);
  };

  /* --------------------------------------------------------------------------
   * Helper Functions (Formatting)
   * -------------------------------------------------------------------------- */

  /**
   * Helper function to find a specific draft from the table data.
   * * @param draftId - The unique identifier of the draft to locate.
   * @returns The draft object if found, otherwise undefined.
   */
  const findDraftData = (draftId: number) => {
    return tableData.find((draft) => draft.id === draftId);
  };

  /**
   * Helper function to show a success notification
   */
  const showSuccessNotification = ({
    title = "Success",
    message = "",
  }: {
    title: string;
    message: string;
  }) => {
    dispatch(
      notifyUser({
        title,
        message,
      }),
    );
  };

  const getTableWrapperClass = (isLoading: boolean) =>
    `${styles["draft-table__table-wrapper"]} ${
      isLoading ? styles["draft-table__table-wrapper--loading"] : ""
    }`;

  return (
    <div className={styles["draft-table"]}>
      <div className={getTableWrapperClass(isLoading)}>
        <TableContainer sx={{ overflow: "hidden" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow className={`drafts-table-header`}>
                <TableCell>Credit Amount</TableCell>
                <TableCell>Credit Quantity</TableCell>
                <TableCell>Saved Date/Time</TableCell>
                <TableCell>Successful Entries</TableCell>
                <TableCell>Error Entries</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            {/* Loading State */}
            {isLoading ? (
              <TableBody>
                <TableRow>
                  <TableCell
                    colSpan={6}
                    sx={{ border: "none", maxHeight: "252px" }}
                  >
                    <CircularProgress
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                      }}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : hasData ? (
              /* Data Found State */
              <TableBody>
                {tableData.map((draft, index) => (
                  <TableRow key={index} className={`draft-table-body`}>
                    <TableCell>
                      {draft?.currency} {draft?.gross_amount}
                    </TableCell>
                    <TableCell>
                      {draft?.quantity === "0" ? "-" : draft?.quantity}
                    </TableCell>
                    <TableCell>
                      <div className={styles["date-time-wrapper"]}>
                        <div className={styles["date-time-wrapper__date"]}>
                          {formatDate(draft?.saved_on)}
                        </div>
                        <div className={styles["date-time-wrapper__time"]}>
                          {formatTime(draft?.saved_on, "Asia/Dubai", {
                            ampm: false,
                          })}{" "}
                          UAE
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      className={
                        draft?.success_count != 0
                          ? styles["count__success"]
                          : styles["count--disabled"]
                      }
                    >
                      {draft?.success_count != 0 ? draft?.success_count : "-"}
                    </TableCell>
                    <TableCell
                      className={
                        draft?.error_count != "0"
                          ? styles["count__error"]
                          : styles["count--disabled"]
                      }
                    >
                      {draft?.error_count !== "0" ? draft?.error_count : "-"}
                    </TableCell>
                    <TableCell>
                      <div className={styles["action-wrapper"]}>
                        {/* Conditional rendering for Send/Submit icon based on errors */}
                        {draft?.error_count !== "0" ? (
                          <StyledTooltip>
                            <div
                              className={
                                styles["action-wrapper__icon--disabled"]
                              }
                            >
                              <SideBarThirdIcon height="16" width="16" />
                            </div>
                          </StyledTooltip>
                        ) : (
                          <SubmitStyledTooltip>
                            <button
                              onClick={() => handleSendDrafts(draft?.id)}
                              className={styles["action-wrapper__icon"]}
                              disabled={isSending || isSummaryLoading}
                            >
                              {(isSending || isSummaryLoading) &&
                              selectedDraftId === draft?.id?.toString() ? (
                                <CircularProgress
                                  sx={{ color: "#0e0f0c" }}
                                  size={16}
                                />
                              ) : (
                                <SideBarThirdIcon height="16" width="16" />
                              )}
                            </button>
                          </SubmitStyledTooltip>
                        )}

                        <button
                          onClick={() => handleEditDrafts(draft?.id)}
                          className={styles["action-wrapper__icon"]}
                        >
                          <EditIcon />
                        </button>
                        <button
                          onClick={() => handleRemoveDrafts(draft?.id)}
                          className={styles["action-wrapper__icon"]}
                        >
                          {isRemoving &&
                          selectedDraftId === draft?.id.toString() ? (
                            <CircularProgress
                              sx={{ color: "#0e0f0c" }}
                              size={16}
                            />
                          ) : (
                            <TrashIcon />
                          )}
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              /* No Data Found State */
              <EmptyTable colSpan={6} />
            )}
          </Table>
        </TableContainer>

        {/* Modal for draft deletion confirmation */}
        {isRemoveModalOpen && (
          <RemoveModal
            onClose={handleCloseRemoveModal}
            isSubmitting={isRemoving}
            onSubmit={handleConfirmRemove}
            draftId={selectedDraftId ?? ""}
          />
        )}

        {/* Modal for draft confirmation */}
        {isConfirmModalOpen && (
          <ConfirmModal
            onClose={handleCloseConfirmModal}
            onSubmit={handleConfirmSubmit}
            isSubmitting={isSending}
            warnings={summaryData?.data?.warnings ?? []}
          />
        )}
      </div>
    </div>
  );
};

export default DraftsTable;
